import { useCallback, useState } from "react"
import { Platform, StyleSheet, TextStyle, View, ViewStyle } from "react-native"

import type { LayoutChangeEvent } from "react-native"
import { useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { CloseButton } from "src/components/CloseButton"
import { Text } from "src/components/Ignite"
import { RoleSwitcher } from "src/components/RoleSwitcher"
import { SidekickView } from "src/components/SidekickView"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { backOrToRoot } from "src/utils/routerHelpers"

/**
 * ChangeRoleScreen - A full-screen role switcher that uses SidekickView for consistent UX.
 *
 * Features:
 * - Avatar overlay with speech integration
 * - Talk/Type/Menu button cluster
 * - Role selection via RoleSwitcher component
 */
export default function ChangeRoleScreen() {
  const insets = useSafeAreaInsets()
  const { themed, navTheme } = useAppTheme()

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  // State to hide avatar during role switch
  const [hideAvatar, setHideAvatar] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(60)

  const handleHeaderLayout = useCallback((e: LayoutChangeEvent) => {
    setHeaderHeight(e.nativeEvent.layout.height)
  }, [])

  const handleClose = useCallback(() => {
    backOrToRoot()
  }, [])

  // Callbacks for RoleSwitcher
  const handleSwitchStart = useCallback(() => {
    setHideAvatar(true)
  }, [])

  const handleSwitchError = useCallback(() => {
    setHideAvatar(false)
  }, [])

  return (
    <View style={themed($container)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />

      <SidekickView
        showAvatar={!hideAvatar}
        autoShowAvatarOnSpeech={!hideAvatar}
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground={true}
        style={themed($container)}
        onMenuPress={handleClose}
        riveOwner="video"
        controlsConfig={{ type: "none" }}
      >
        {/* Custom Header - positioned above the content */}
        <View
          style={[
            themed($headerContainer),
            {
              top: insets.top,
              left: insets.left,
              right: insets.right,
            },
          ]}
          pointerEvents="box-none"
          onLayout={handleHeaderLayout}
        >
          <Text style={themed($headerTitle)}>Change Role</Text>
          <CloseButton
            onPress={handleClose}
            style={{ position: "absolute", top: 22, right: 16 }}
          />
        </View>
        {/* Role Switcher Content */}
        <View
          style={[
            themed($contentWrapper),
            {
              paddingTop: headerHeight + 10,
              paddingBottom: 140,
            },
          ]}
        >
          <RoleSwitcher
            onSwitchStart={handleSwitchStart}
            onSwitchError={handleSwitchError}
            style={styles.roleSwitcher}
          />
        </View>
      </SidekickView>
    </View>
  )
}

const styles = StyleSheet.create({
  roleSwitcher: {
    paddingTop: 0, // Override default padding since we handle it in contentWrapper
  },
})

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $headerContainer: ThemedStyle<ViewStyle> = () => ({
  position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
  zIndex: 2100,
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: 16,
  height: 80,
  // No backgroundColor — see menu.tsx for rationale (avoid occluding the
  // tab-mode avatar that lives in MediaPlayerShell's foreground layer).
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
})

const $contentWrapper: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})
